import type { InterfaceEntity, Navigation } from '@unifyapps/defs/types/interface';
import {
  ApplicationType,
  NavigationResolution,
  NavigatorType,
} from '@unifyapps/defs/types/interface';
import { produce } from 'immer';
import { InterfaceNavigationHelper } from '../InterfaceNavigationHelper';

const DEFAULT_BREAKPOINT_VISIBILITY = [
  NavigationResolution.XL,
  NavigationResolution.LG,
  NavigationResolution.MD,
  NavigationResolution.SM,
];

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- false positive
export class CustomNavHelper {
  /**
   * Checks if custom navigation is enabled for the interface.
   * Custom navigation allows mobile apps to use a user-defined module
   * instead of the built-in bottom navigation.
   */
  static isEnabled(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
    applicationType?: ApplicationType,
    checkModuleId?: boolean,
  ): boolean {
    const effectiveResolution = resolution ?? NavigationResolution.XL;
    const navigationBarConfig =
      interfaceRecord.properties.navigation?.appearance?.[effectiveResolution]?.navigationBar;
    const { navigatorModuleId, navigatorType } = navigationBarConfig ?? {};

    if (applicationType === ApplicationType.MOBILE || resolution === NavigationResolution.SM) {
      return Boolean(navigatorModuleId);
    }

    if (checkModuleId) {
      return Boolean(navigatorType === NavigatorType.Custom && navigatorModuleId);
    }

    return Boolean(navigatorType === NavigatorType.Custom);
  }

  static isNavModule(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
    moduleId?: string,
  ): boolean {
    if (!moduleId) {
      return false;
    }
    const navigationBarConfig =
      interfaceRecord.properties.navigation?.appearance?.[resolution]?.navigationBar;
    const { navigatorModuleId } = navigationBarConfig ?? {};
    return moduleId === navigatorModuleId;
  }

  /**
   * Gets the module ID for custom navigation if configured.
   */
  static getModuleId(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
  ): string | undefined {
    const navigationBarConfig =
      interfaceRecord.properties.navigation?.appearance?.[resolution]?.navigationBar;
    const { navigatorModuleId } = navigationBarConfig ?? {};
    return navigatorModuleId;
  }

  /**
   * Cleans up custom navigation config when the custom navigation module is deleted.
   * Iterates through all 4 navigation resolutions (XL, LG, MD, SM) and removes
   * the navigatorModuleId from all resolutions where it matches the deleted module.
   */
  static cleanupModule(navigation: Navigation, deletedModuleId: string): Navigation {
    if (!navigation.appearance) {
      return navigation;
    }

    const hasMatchingModule = DEFAULT_BREAKPOINT_VISIBILITY.some(
      (resolution) =>
        navigation.appearance?.[resolution]?.navigationBar?.navigatorModuleId === deletedModuleId,
    );

    if (!hasMatchingModule) {
      return navigation;
    }

    return produce(navigation, (draft) => {
      for (const resolution of DEFAULT_BREAKPOINT_VISIBILITY) {
        if (draft.appearance?.[resolution]?.navigationBar?.navigatorModuleId === deletedModuleId) {
          draft.appearance[resolution].navigationBar.navigatorModuleId = undefined;
        }
      }
    });
  }

  /**
   * Expose primaryNav Items and Brand Navigation Item separately
   */
  static getPrimaryNavigationByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
  ) {
    const primaryNavItems = InterfaceNavigationHelper.getPrimaryNavigationByResolution(
      interfaceRecord,
      resolution,
    );
    return primaryNavItems.filter((item) => item.id !== 'BRAND_NAVIGATION');
  }

  /**
   * Expose secondaryNav Items and User Profile Item separately
   */
  static getSecondaryNavigationByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
  ) {
    const secondaryNavItems = InterfaceNavigationHelper.getSecondaryItemsByResolution(
      interfaceRecord,
      resolution,
    );
    return secondaryNavItems?.filter((item) => item.id !== 'userMenu');
  }

  /**
   * When creating custom navigation module, get the default active nav item and group nav item
   * to be passed to inputSchema/module inputs
   * Not used currently
   */
  static getDefaultActiveNavItem({
    interfaceRecord,
    resolution = NavigationResolution.XL,
  }: {
    interfaceRecord: InterfaceEntity;
    resolution?: NavigationResolution;
  }) {
    const primaryNavItems = CustomNavHelper.getPrimaryNavigationByResolution(
      interfaceRecord,
      resolution,
    );

    const secondaryNavItems = CustomNavHelper.getSecondaryNavigationByResolution(
      interfaceRecord,
      resolution,
    );

    if (!primaryNavItems.length && !secondaryNavItems?.length) {
      return { activeNavItemId: undefined, activeGroupNavItemId: undefined };
    }
    const mergedNavItems = [...primaryNavItems, ...(secondaryNavItems ?? [])];

    if (mergedNavItems[0].type === 'GROUP') {
      return {
        defaultActiveNavItemId:
          mergedNavItems[0].items.length > 0 ? mergedNavItems[0].items[0].id : undefined,
        defaultActiveGroupNavItemId: mergedNavItems[0].id,
      };
    }
    return { defaultActiveNavItemId: mergedNavItems[0].id, defaultActiveGroupNavItemId: undefined };
  }
}
